import { useEffect, useMemo, useRef, useState } from 'react';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { abbreviateBook, buildBibleMapRows } from '../../utils/bibleMap';
import './BibleProgressCalendar.css';

// Fallbacks for the first paint before the host has been measured.
// Rough tooltip size, used only to decide whether it fits above the cell.
const TOOLTIP_WIDTH = 220;
const TOOLTIP_HEIGHT = 130;

const DEFAULT_LAYOUT = { columns: 10, abbreviate: false };

/**
 * One continuous chapter grid per testament. Books flow into each other with
 * no break; an alternating band tint and a stepped boundary line mark where
 * one ends and the next begins, and the left gutter names the book that owns
 * the first column of each row. Hovering or tapping that label highlights
 * every chapter in the book.
 *
 * @param {Object} props
 * @param {Set<number>|null} [props.neededChapterIds] chapters still needed for
 *   the community's next completion — highlighted as "still needed" (WS1).
 * @param {Set<number>|null} [props.flashChapterIds] when set, exactly these
 *   cells flash, replacing the built-in "count went up" flash (the replay
 *   drives it at a pace the 2s timer cannot follow).
 * @param {boolean} [props.replay] zoomed-out map for the season replay: small
 *   cells with no chapter numbers, so the whole Bible fits on one screen.
 */
const BibleProgressCalendar = ({
  chapters,
  chapterReadCounts,
  neededChapterIds = null,
  flashChapterIds = null,
  replay = false
}) => {
  const [hoveredChapter, setHoveredChapter] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const [activeBook, setActiveBook] = useState(null);
  const [legendOpen, setLegendOpen] = useState(false);
  // Touch and keyboard pin the highlight; a mouse hover does not.
  const bookPinnedRef = useRef(false);
  const labelPointerTypeRef = useRef('mouse');
  const legendRef = useRef(null);
  const hostRef = useRef(null);
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  // Chapters whose count just went up, so a log by anyone visibly lights a cell.
  const [recentlyChanged, setRecentlyChanged] = useState(() => new Set());
  const previousCountsRef = useRef(chapterReadCounts);

  // Column count comes from the measured width and the slot/gutter sizes the
  // stylesheet sets for the current breakpoint, so the grid always fills the
  // row and the gutter only switches to abbreviations when it gets narrow.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    const measure = () => {
      const styles = getComputedStyle(host);
      const slot = parseFloat(styles.getPropertyValue('--map-slot')) || 40;
      const gutter = parseFloat(styles.getPropertyValue('--map-gutter')) || 104;
      const width = host.clientWidth;
      if (!width) return;
      // 2px for the grid's own border. Whatever is left after whole columns
      // widens the gutter, so judge label room by the gutter's real width.
      const columns = Math.max(4, Math.floor((width - gutter - 2) / slot));
      const actualGutter = width - 2 - columns * slot;
      const abbreviate = actualGutter < 100;
      setLayout((current) => (
        current.columns === columns && current.abbreviate === abbreviate
          ? current
          : { columns, abbreviate }
      ));
    };

    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!activeBook || !bookPinnedRef.current) return undefined;

    const dismiss = (event) => {
      if (event.target.closest?.('.bible-map-gutter.has-label')) return;
      bookPinnedRef.current = false;
      setActiveBook(null);
    };

    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [activeBook]);

  useEffect(() => {
    if (!legendOpen) return undefined;

    const closeOnOutside = (event) => {
      if (!legendRef.current?.contains(event.target)) setLegendOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setLegendOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [legendOpen]);

  useEffect(() => {
    const previous = previousCountsRef.current;
    previousCountsRef.current = chapterReadCounts;
    if (flashChapterIds || previous === chapterReadCounts) return undefined;

    const changed = new Set();
    for (const [chapterId, count] of Object.entries(chapterReadCounts || {})) {
      if ((previous?.[chapterId] || 0) < count) changed.add(Number(chapterId));
    }
    if (changed.size === 0) return undefined;

    setRecentlyChanged(changed);
    const timer = setTimeout(() => setRecentlyChanged(new Set()), 2000);
    return () => clearTimeout(timer);
  }, [chapterReadCounts, flashChapterIds]);

  const getChapterStatus = (chapterId) => {
    const readCount = chapterReadCounts[chapterId] || 0;
    if (readCount === 0) return 'unread';
    if (readCount === 1) return 'read-1';
    if (readCount <= 3) return 'read-2-3';
    if (readCount <= 5) return 'read-4-5';
    if (readCount <= 10) return 'read-6-10';
    if (readCount <= 20) return 'read-11-20';
    if (readCount <= 35) return 'read-21-35';
    if (readCount <= 50) return 'read-36-50';
    return 'read-50-plus';
  };

  const getChapterClassName = (chapter) => {
    const status = getChapterStatus(chapter.id);
    const isHovered = hoveredChapter?.id === chapter.id;
    const isNeeded = neededChapterIds?.has(chapter.id);
    const justChanged = (flashChapterIds || recentlyChanged).has(chapter.id);
    return [
      'progress-chapter-cell',
      status,
      isHovered ? 'hovered' : '',
      isNeeded ? 'needed' : '',
      justChanged ? 'just-read' : ''
    ].filter(Boolean).join(' ');
  };

  // Anchor the tooltip to the cell: above it, or below when there is no room,
  // and kept inside the viewport horizontally.
  const handleChapterHover = (chapter, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const halfWidth = TOOLTIP_WIDTH / 2;
    const left = Math.min(
      Math.max(rect.left + rect.width / 2, halfWidth + 8),
      window.innerWidth - halfWidth - 8
    );
    const above = rect.top > TOOLTIP_HEIGHT + 16;
    setTooltipPosition({
      left,
      top: above ? rect.top - 8 : rect.bottom + 8,
      above
    });
    setHoveredChapter(chapter);
  };

  const handleChapterLeave = () => {
    setHoveredChapter(null);
  };

  const oldTestamentChapters = useMemo(
    () => chapters.filter(ch => ch.testament === 'Old'),
    [chapters]
  );
  const newTestamentChapters = useMemo(
    () => chapters.filter(ch => ch.testament === 'New'),
    [chapters]
  );
  const oldTestamentRows = useMemo(
    () => buildBibleMapRows(oldTestamentChapters, layout.columns),
    [oldTestamentChapters, layout.columns]
  );
  const newTestamentRows = useMemo(
    () => buildBibleMapRows(newTestamentChapters, layout.columns),
    [newTestamentChapters, layout.columns]
  );

  const countRead = (list) => list.filter(ch => chapterReadCounts[ch.id] > 0).length;
  const countReads = (list) => list.reduce((sum, ch) => sum + (chapterReadCounts[ch.id] || 0), 0);

  const pinBook = (book) => {
    setActiveBook((current) => {
      const next = current === book ? null : book;
      bookPinnedRef.current = Boolean(next);
      return next;
    });
  };

  const onLabelPointerDown = (event) => {
    labelPointerTypeRef.current = event.pointerType;
  };

  const onLabelPointerEnter = (event, book) => {
    if (event.pointerType !== 'mouse') return;
    bookPinnedRef.current = false;
    setActiveBook(book);
  };

  const onLabelPointerLeave = (event) => {
    if (event.pointerType !== 'mouse' || bookPinnedRef.current) return;
    setActiveBook(null);
  };

  const onLabelClick = (book) => {
    if (labelPointerTypeRef.current === 'mouse') return;
    pinBook(book);
  };

  const onLabelKeyDown = (event, book) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    pinBook(book);
  };

  const renderRow = (row) => {
    const bookActive = activeBook === row.book;
    const gutterClass = [
      'bible-map-gutter',
      row.band ? 'band-b' : 'band-a',
      row.boundaryTop ? 'boundary-top' : '',
      row.showLabel ? 'has-label' : '',
      bookActive ? 'book-active' : ''
    ].filter(Boolean).join(' ');

    return [
      <div
        key={`gutter-${row.key}`}
        className={gutterClass}
        title={row.book}
        role={row.showLabel ? 'button' : undefined}
        tabIndex={row.showLabel ? 0 : undefined}
        aria-pressed={row.showLabel ? bookActive : undefined}
        aria-label={row.showLabel ? `Highlight ${row.book}` : undefined}
        onPointerDown={row.showLabel ? onLabelPointerDown : undefined}
        onPointerEnter={row.showLabel ? (event) => onLabelPointerEnter(event, row.book) : undefined}
        onPointerLeave={row.showLabel ? onLabelPointerLeave : undefined}
        onClick={row.showLabel ? () => onLabelClick(row.book) : undefined}
        onKeyDown={row.showLabel ? (event) => onLabelKeyDown(event, row.book) : undefined}
      >
        {row.showLabel && (layout.abbreviate ? abbreviateBook(row.book) : row.book)}
      </div>,
      ...row.cells.map(cell => (
        <div
          key={cell.chapter.id}
          className={[
            'bible-map-slot',
            cell.band ? 'band-b' : 'band-a',
            cell.boundaryTop ? 'boundary-top' : '',
            cell.boundaryLeft ? 'boundary-left' : '',
            activeBook === cell.chapter.book ? 'book-active' : ''
          ].filter(Boolean).join(' ')}
        >
          <div
            className={getChapterClassName(cell.chapter)}
            onMouseEnter={(event) => handleChapterHover(cell.chapter, event)}
            onClick={(event) => handleChapterHover(cell.chapter, event)}
            onMouseLeave={handleChapterLeave}
            title={`${cell.chapter.book} ${cell.chapter.chapter}`}
          >
            {!replay && cell.chapter.chapter}
          </div>
        </div>
      ))
    ];
  };

  const renderTestament = (title, list, rows) => (
    <div className="testament-section">
      <div className="testament-header">
        <h3>{title}</h3>
        <span className="testament-progress">
          {countRead(list)} / {list.length} chapters ({countReads(list)} total reads)
        </span>
      </div>
      <div
        className="bible-map-grid"
        style={{ '--map-columns': layout.columns }}
        role="group"
        aria-label={`${title} chapters`}
      >
        {rows.map(renderRow)}
      </div>
    </div>
  );

  return (
    <div className={`bible-progress-calendar${replay ? ' replay' : ''}`} ref={hostRef}>
      <div className="calendar-legend-control" ref={legendRef}>
        <button
          type="button"
          className="calendar-legend-toggle"
          aria-expanded={legendOpen}
          aria-controls="calendar-color-key"
          onClick={() => setLegendOpen((open) => !open)}
        >
          <IoInformationCircleOutline size={22} aria-hidden="true" />
          <span className="visually-hidden">
            {legendOpen ? 'Hide chapter color key' : 'Show chapter color key'}
          </span>
        </button>

        {legendOpen && (
          <div
            id="calendar-color-key"
            className="calendar-legend"
            role="region"
            aria-label="Chapter color key"
          >
            {neededChapterIds && neededChapterIds.size > 0 && (
              <div className="legend-item">
                <div className="legend-color needed"></div>
                <span>Still needed</span>
              </div>
            )}
            <div className="legend-item">
              <div className="legend-color unread"></div>
              <span>Unread</span>
            </div>
            <div className="legend-item">
              <div className="legend-color read-1"></div>
              <span>1x</span>
            </div>
            <div className="legend-item">
              <div className="legend-color read-2-3"></div>
              <span>2-3x</span>
            </div>
            <div className="legend-item">
              <div className="legend-color read-4-5"></div>
              <span>4-5x</span>
            </div>
            <div className="legend-item">
              <div className="legend-color read-6-10"></div>
              <span>6-10x</span>
            </div>
            <div className="legend-item">
              <div className="legend-color read-11-20"></div>
              <span>11-20x</span>
            </div>
            <div className="legend-item">
              <div className="legend-color read-21-35"></div>
              <span>21-35x</span>
            </div>
            <div className="legend-item">
              <div className="legend-color read-36-50"></div>
              <span>36-50x</span>
            </div>
            <div className="legend-item">
              <div className="legend-color read-50-plus"></div>
              <span>50+x</span>
            </div>
          </div>
        )}
      </div>

      {/* Hover Tooltip */}
      {hoveredChapter && tooltipPosition && (
        <div
          className={`chapter-tooltip${tooltipPosition.above ? ' above' : ' below'}`}
          style={{ left: tooltipPosition.left, top: tooltipPosition.top }}
        >
          <strong>{hoveredChapter.book} {hoveredChapter.chapter}</strong>
          <div className="tooltip-stats">
            <span>Read {chapterReadCounts[hoveredChapter.id] || 0} time{(chapterReadCounts[hoveredChapter.id] || 0) !== 1 ? 's' : ''}</span>
            <span>{hoveredChapter.testament} Testament</span>
            <span>{hoveredChapter.verseCount} verses</span>
            {neededChapterIds?.has(hoveredChapter.id) && (
              <span className="tooltip-needed">Still needed for the next completion</span>
            )}
          </div>
        </div>
      )}

      {renderTestament('Old Testament', oldTestamentChapters, oldTestamentRows)}
      {renderTestament('New Testament', newTestamentChapters, newTestamentRows)}
    </div>
  );
};

export default BibleProgressCalendar;
